import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useStore } from 'models/Provider';

import {
  createJobLabel,
  deleteJobLabel,
  editJobLabel,
  JobLabelCreate,
  JobLabelEdit,
  loadJobLabels,
} from './requests';

export const useJobLabels = () => {
  return useQuery({
    queryKey: ['job-labels'],
    queryFn: loadJobLabels,
  });
};

type JobLabelError = AxiosError<{
  translations: { language: string; message: string };
  color: string;
}>;

export function useCreateJobLabel() {
  const { t } = useScopedTranslate(locale);
  const { current } = useStore();
  const { showSuccess, showError } = useToaster();
  const queryClient = useQueryClient();

  return useMutation(
    (
      props: JobLabelCreate & {
        onSuccess?: (id: number) => void;
      },
    ) => createJobLabel(props),
    {
      onSuccess: (response, { onSuccess }) => {
        showSuccess(t('newJobLabelSaved'));
        queryClient.invalidateQueries(['job-labels']);
        current.loadInfo();
        onSuccess?.(response.data.id);
      },
      onError: (error: JobLabelError) => {
        showError(
          error.response
            ? ((error.response.data.translations.message || error.response.data.color) as string)
            : t('jobLabelNotSaved'),
        );
      },
    },
  );
}

export function useEditJobLabel() {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const queryClient = useQueryClient();
  const { current } = useStore();

  return useMutation(
    (
      props: JobLabelEdit & {
        onSuccess?: () => void;
      },
    ) => editJobLabel(props),
    {
      onSuccess: (_, { onSuccess }) => {
        showSuccess(t('jobLabelSaved'));
        queryClient.invalidateQueries(['job-labels']);
        current.loadInfo();
        onSuccess?.();
      },
      onError: () => {
        showError(t('jobLabelNotSaved'));
      },
    },
  );
}

type DeleteJobLabelProps = {
  id: number;
  onSuccess?: () => void;
};
export function useDeleteJobLabel(props: DeleteJobLabelProps) {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const queryClient = useQueryClient();

  return useMutation(() => deleteJobLabel(props.id), {
    onSuccess: () => {
      showSuccess(t('jobLabelDeleted'));
      queryClient.invalidateQueries(['job-labels']);
      props.onSuccess?.();
    },
    onError: () => {
      showError(t('jobLabelNotDeleted'));
    },
  });
}

const locale = {
  en: {
    newJobLabelSaved: 'Job label created',
    jobLabelSaved: 'Job label saved',
    jobLabelNotSaved: 'Job label not saved',
    jobLabelDeleted: 'Job label deleted',
    jobLabelNotDeleted: 'Job label not deleted',
  },
  is: {
    newJobLabelSaved: 'Merki starfs búið til',
    jobLabelSaved: 'Merki starfs vistað',
    jobLabelNotSaved: 'Merki starfs ekki vistað',
    jobLabelDeleted: 'Merki starfs eytt',
    jobLabelNotDeleted: 'Merki starfs ekki eytt',
  },
};
